import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CContainer, CSpinner, CRow, CCol } from '@coreui/react';
import { DateRangePicker } from 'rsuite';
import { startOfDay, endOfDay, addDays, subDays, format } from 'date-fns';
import { useDispatch } from 'react-redux';
import Cards from '../../../../base/cards/Cards';
import KPIDetailCard from '../../../Dashboard/Dashboard/KPIDetailCard';
import { CChart } from '@coreui/react-chartjs';
import ChipFilter from '../../../../../components/chips/ChipsFilter';
import { usePricingHook } from '../../../../../hooks/PricingHooks/usePricingHook';
import { getUserInformation } from '../../../../../services/TokenHandler';

const { afterToday } = DateRangePicker;

const EMPTY_DATA = { unitsSold: 0, unitsUsed: 0, contactIndex: 0, productSalesPct: 0, chart: [] };

const Ranges = [
  { label: 'Today', value: [startOfDay(new Date()), endOfDay(new Date())] },
  {
    label: 'Yesterday',
    value: [startOfDay(addDays(new Date(), -1)), endOfDay(addDays(new Date(), -1))]
  },
  { label: '7 Days', value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())] },
  { label: '30 Days', value: [startOfDay(subDays(new Date(), 30)), endOfDay(new Date())] }
];

const ProductHubDashboardV2 = ({ productCode, productLabel }) => {
  const dispatch = useDispatch();
  const { methodCall } = usePricingHook();

  const parsedUserInfo = useMemo(() => {
    const raw = getUserInformation();
    return raw ? JSON.parse(raw) : {};
  }, []);

  const companyId = parsedUserInfo?.companies?.[0]?.id;

  const [dateRange, setDateRange] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiData, setApiData] = useState(EMPTY_DATA);

  const isVip = productCode === 'vipp' || productCode === 'vipo';
  const [vipOutbound, setVipOutbound] = useState(productCode === 'vipo');

  useEffect(() => {
    if (productLabel) {
      dispatch({
        type: 'set',
        breadcrumbs: [
          { name: 'Pricing Rules', link: false },
          { name: productLabel, link: false },
          { name: 'Hub', link: false }
        ]
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productLabel]);

  useEffect(() => {
    if (isVip) {
      setVipOutbound(productCode === 'vipo');
    }
  }, [isVip, productCode]);

  const activeProductCode = isVip ? (vipOutbound ? 'vipo' : 'vipp') : productCode;

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;
    setIsLoading(true);

    const params = { companyId, productCode: activeProductCode };
    if (dateRange) {
      const [from, to] = dateRange;
      params.effectiveDateFrom = format(startOfDay(from), 'yyyy-MM-dd');
      params.effectiveDateTo = format(endOfDay(to), 'yyyy-MM-dd');
    }

    methodCall('getProductHubStats', params).then(({ data }) => {
      if (!cancelled) {
        if (data) setApiData(data);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, activeProductCode, dateRange]);

  const chartLabels = (apiData.chart || []).map((c) => c.label);

  const chartDatasets = [
    {
      label: 'Registrations',
      data: (apiData.chart || []).map((c) => c.registrations),
      borderColor: '#B2F3D3',
      pointBackgroundColor: '#B2F3D3',
      pointBorderColor: '#fff',
      backgroundColor: '#B2F3D333',
      tension: 0.4,
      fill: true
    },
    {
      label: 'Updates',
      data: (apiData.chart || []).map((c) => c.updates),
      borderColor: '#FFD166',
      pointBackgroundColor: '#FFD166',
      pointBorderColor: '#fff',
      backgroundColor: '#FFD16633',
      tension: 0.4,
      fill: true
    },
    {
      label: 'Consumption',
      data: (apiData.chart || []).map((c) => c.consumption),
      borderColor: '#A78BFA',
      pointBackgroundColor: '#A78BFA',
      pointBorderColor: '#fff',
      backgroundColor: '#A78BFA11',
      tension: 0.4,
      fill: true
    }
  ];

  const lineLegendItems = chartDatasets.map((ds) => ({
    label: ds.label,
    color: ds.pointBackgroundColor || ds.borderColor
  }));

  const kpiCards = [
    { title: 'Units Sold', totalValue: apiData.unitsSold, decimals: 0 },
    { title: 'Units Used', totalValue: apiData.unitsUsed, decimals: 0 },
    { title: 'Contact Ratio', totalValue: apiData.contactIndex, decimals: 2 },
    { title: 'Unused Product (%)', totalValue: apiData.productSalesPct, decimals: 2, suffix: '%' }
  ];

  return (
    <div className="Dashboard-Content">
      <div className="Dashboard-Content-Container">
        <div
          className="TabContainer"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: '16px',
            flexWrap: 'wrap'
          }}
        >
          {isVip && (
            <div style={{ minWidth: 260 }}>
              <ChipFilter
                options={[
                  { value: 'inbound', label: 'Inbound' },
                  { value: 'outbound', label: 'Outbound' }
                ]}
                selected={[vipOutbound ? 'outbound' : 'inbound']}
                onToggle={(value) => setVipOutbound(value === 'outbound')}
              />
            </div>
          )}
          <div style={{ width: 340, maxWidth: '100%', marginLeft: 'auto' }}>
            <DateRangePicker
              block
              format="MM/dd/yyyy"
              appearance="default"
              placement="auto"
              preventOverflow
              size="lg"
              shouldDisableDate={afterToday()}
              ranges={Ranges}
              value={dateRange}
              onChange={(range) => setDateRange(range)}
              placeholder="Filter by date range (optional)"
            />
          </div>
        </div>
      </div>

      <CContainer
        fluid
        className="Dashboard-Content-Body"
        style={{ position: 'relative', minHeight: '400px' }}
      >
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              zIndex: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backdropFilter: 'blur(2px)'
            }}
          >
            <CSpinner color="info" variant="grow" />
          </div>
        )}

        <div className="dashboard-main">
          <CRow className="g-4 mb-5">
            {kpiCards.map((card) => (
              <CCol key={card.title} xs={12} sm={6} xl={3}>
                <KPIDetailCard {...card} />
              </CCol>
            ))}
          </CRow>

          <Cards>
            <>
              <div className="description">
                <h3 style={{ fontSize: '16px', fontWeight: 700, lineHeight: '16px' }}>
                  Registrations, Updates & Consumption
                </h3>
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    color: '#646464',
                    height: '24px'
                  }}
                />
              </div>

              <div className="g1 dashboard-main__line-chart-container">
                <CChart
                  type="line"
                  data={{
                    labels: chartLabels,
                    datasets: chartDatasets
                  }}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: '#ffffff',
                        titleColor: '#000000',
                        bodyColor: '#000000',
                        borderColor: '#e0e0e0',
                        borderWidth: 1,
                        padding: 12,
                        displayColors: true,
                        boxPadding: 6,
                        usePointStyle: true,
                        cornerRadius: 8,
                        titleFont: { weight: '600', size: 14 },
                        bodyFont: { size: 14 }
                      }
                    },
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }}
                  height={300}
                />
              </div>

              <div className="dashboard-main__line-legend mt-4">
                {lineLegendItems.map((item) => (
                  <div key={item.label} className="dashboard-main__legend-item">
                    <span
                      className="dashboard-main__legend-color"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="dashboard-main__legend-label">{item.label}</span>
                  </div>
                ))}
              </div>
            </>
          </Cards>
        </div>
      </CContainer>
    </div>
  );
};

ProductHubDashboardV2.propTypes = {
  productCode: PropTypes.string.isRequired,
  productLabel: PropTypes.string
};

export default ProductHubDashboardV2;
